import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import config from 'config';
import Auth from 'modules/Auth';
import { v4 as uuidv4 } from 'uuid';
import { SESSION_RECORDER_ENABLED } from 'components/common/SessionRecorderButton/consts';
import Local from './Local';
import { httpTransactionMap, makeHttpTransactionKey } from './api/axiosNetworkDebuggerInterceptor';

const isDatadogEnabled =
  config.environment !== 'local' && config.environment !== 'dev' && config.environment !== 'testing';
const isLoggedEnvironment = config.environment === 'uat' || config.environment === 'prd';

// Handles both full URLs (https://example.com/path) and relative paths (/path)
const matchesBaseUrl = (url: string, baseUrl: string): boolean => {
  // If baseUrl is a full URL (starts with http:// or https://), use startsWith
  if (baseUrl.startsWith('http://') || baseUrl.startsWith('https://')) {
    return url.startsWith(baseUrl);
  }
  // If baseUrl is a relative path, check if the URL's pathname includes it
  try {
    const urlObj = new URL(url);
    return urlObj.pathname.startsWith(baseUrl);
  } catch {
    // Fallback: if URL parsing fails, check if the URL string includes the baseUrl
    return url.includes(baseUrl);
  }
};

export const dataDogLogExternalPublisherId = (): void => {
  if (isLoggedEnvironment) {
    datadogRum.setGlobalContextProperty('publisherExternalId', Local.getEnvironmentId());
  }
};

export const dataDogInitialise = (): void => {
  if (!isDatadogEnabled) return;

  const { email, userName } = Auth.getData();

  datadogRum.init({
    ...config.dataDog,
    env: config.environment,
    version: VERSION,
    allowedTracingUrls: [
      (url) => matchesBaseUrl(url, config.api.BASE_URL),
      (url) => matchesBaseUrl(url, config.dsm.BASE_URL),
    ],
    beforeSend: (event) => {
      if (SESSION_RECORDER_ENABLED && event.type === 'resource' && event.resource?.method && event.resource?.url) {
        if (!matchesBaseUrl(event.resource.url, config.api.BASE_URL)) {
          return true;
        }

        const httpTransactionKey = makeHttpTransactionKey(event.resource.method, event.resource.url);

        const httpTransaction = httpTransactionMap.get(httpTransactionKey);
        if (httpTransaction) {
          event.context!.request = {
            body: httpTransaction.requestBody,
          };
          event.context!.response = {
            body: httpTransaction.responseBody,
          };
        }

        httpTransactionMap.delete(httpTransactionKey);
      }
      return true;
    },
  } as RumInitConfiguration);
  datadogRum.setUser({
    id: uuidv4(),
    email,
    name: userName,
  });

  if (isLoggedEnvironment) {
    datadogLogs.init({
      ...config.dataDog,
      env: config.environment,
      version: VERSION,
      sessionSampleRate: 100,
    });
    datadogLogs.setUser({
      email,
      name: userName,
    });
  }
};
